// UI-assets — dòng lỗi gọn của bước Tài sản (thay hộp đỏ liệt kê khoá + mã thô). THUẦN (không antd).
// Lý do bằng tiếng Việt; chữ thô của máy chủ (error.message / mã) CHỈ nằm ở `detail` để hiện trong Tooltip. Nút «Chạy lại» dùng hành động có sẵn của đầu mục bước (RUN_FAILED, rồi RUN_MISSING).
import type { WorkflowStatus } from '../../types/filmWorkflow';
import { flagInfoOf } from './workflowFlagUtils';
import { typeLabelOf, type PrimaryCall, type SecondaryAction } from './workflowPrimaryPolicy';

/** Cảnh báo cấp plan «hồ sơ phong cách chờ duyệt» — thẻ hồ sơ ở cuối bước đã nói việc đó, nên thân bước Tài sản không lặp lại. */
export const ASSETS_STYLE_ALERT_CODE = 'STYLE_PROFILE_NOT_APPROVED';

export interface AssetsFailureRetry { call: PrimaryCall | null; enabled: boolean; reason?: string }
export interface AssetsFailureLine { text: string; detail: string | null; count: number; retry: AssetsFailureRetry | null }

const REASON_BY_CODE: Record<string, string> = {
  ASSET_PROPOSAL_INVALID: 'Máy chưa chuẩn bị xong danh sách tài sản',
  BLOCKED_UPSTREAM: 'Còn thiếu kết quả của bước trước',
  BLOCKED_INPUT: 'Còn thiếu thông tin đầu vào',
};
const REASON_BY_OUTCOME: Record<string, string> = {
  FAILED: 'Lần chạy tài sản chưa xong',
  BLOCKED: 'Thiếu đầu vào để làm tiếp',
  UNCERTAIN: 'Có mục chưa rõ kết quả — cần đối soát',
  NOT_RUN_STOP: 'Lần chạy tài sản đã dừng giữa chừng',
  NOT_RUN_BUDGET: 'Lần chạy tài sản hết lượt cho phép',
  NOT_RUN_INTERRUPTED: 'Lần chạy tài sản bị ngắt',
};
const PRIORITY = ['FAILED', 'BLOCKED', 'NOT_RUN_BUDGET', 'NOT_RUN_INTERRUPTED', 'NOT_RUN_STOP', 'UNCERTAIN'];

export function assetsFailureLineOf(status: WorkflowStatus | null | undefined, actions: SecondaryAction[]): AssetsFailureLine | null {
  const bad = (status?.stepRuns?.ASSETS?.objects ?? []).filter((o) => flagInfoOf({ outcome: o.outcome, code: o.error?.code, message: o.error?.message }).kind === 'ERROR');
  if (bad.length === 0) return null;
  const first = [...bad].sort((a, b) => PRIORITY.indexOf(a.outcome) - PRIORITY.indexOf(b.outcome))[0];
  const reason = (first.error?.code && REASON_BY_CODE[first.error.code]) || REASON_BY_OUTCOME[first.outcome] || 'Lần chạy tài sản chưa xong';
  const rawMsg = (first.error?.message ?? '').trim();
  const detail = rawMsg || first.error?.code || null;
  const text = bad.length > 1 ? `${reason} · ${bad.length} mục` : reason;
  const onlyUncertain = bad.every((o) => o.outcome === 'UNCERTAIN');
  let retry: AssetsFailureRetry | null = null;
  if (!onlyUncertain) {
    const usable = ['RUN_FAILED', 'RUN_MISSING'].map((k) => actions.find((a) => a.key === k)).filter((a): a is SecondaryAction => !!a);
    const ok = usable.find((a) => a.enabled && a.call);
    if (ok) retry = { call: ok.call, enabled: true };
    else if (usable.length) retry = { call: null, enabled: false, ...(usable[0].reason ? { reason: usable[0].reason } : {}) };
  }
  return { text, detail, count: bad.length, retry };
}

/** Tên hiển thị của một mục tài sản: tên thật, thiếu thì loại («Nhân vật», «Bối cảnh»…) — KHÔNG BAO GIỜ lộ khoá thô (`missing:look:<uuid>:…`). */
export const assetNameOf = (name: string | null | undefined, key: string): string => (name && name.trim()) || typeLabelOf(key);
